import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'equasion-lib';
import { FiAlertTriangle, FiCameraOff, FiCheckCircle, FiLink, FiX } from 'react-icons/fi';
import ScanModeSwitch, { LaserModePanel } from '@/lib/shared-coms/ScanModeSwitch';
import { useScanner, SCAN_PROFILE } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { SCAN_MODE } from '@/lib/scanning/scanMode';
import { buzz, playFailTone, playSuccessTone } from '@/lib/scanning/feedback';
import {
  pickTicketCodeFromScan,
  soLabel,
  type CombineOrder
} from '@/lib/combineShipment';
import type { CombineActionResult } from './useCombineShipment';
import '@/lib/shared-coms/ZoneScanner/ZoneScanner.scss';
import './CombineShipment.scss';

type Status =
  | { kind: 'scanning' }
  | { kind: 'combining'; code: string }
  | { kind: 'rejected'; code: string; message: string }
  | { kind: 'combined'; orderNumber: string; alreadyCombined: boolean };

const click = (onClick: () => void) => ({ onClick });

/** How long the "combined" confirmation stays up before the overlay closes itself. */
export const COMBINE_CONFIRM_MS = 900;

/**
 * MERGE BY SCAN: a full-screen scanner for the other order's PICK TICKET (its Code 128 sales order
 * number) — the zone scanner's layout and camera hand-off, with the home screen's profile (it reads
 * the pick ticket's barcode). Or the SO number typed into the field at the bottom.
 *
 * Mounting it claims the camera / trigger; unmounting hands it straight back to the Pick or Verify
 * screen underneath (which never sees the pick ticket as an item). The read is sent as it is;
 * the server decides what it names and whether it may be combined, and says why not.
 */
function CombineScanOverlay({
  expected = [],
  clientName,
  onCombine,
  onClose
}: {
  /** The orders the alert is about — shown as what to scan. */
  expected?: CombineOrder[];
  clientName?: string | null;
  onCombine: (code: string) => Promise<CombineActionResult>;
  /** Closed by the X, by Done, or after a combine has been confirmed. */
  onClose: () => void;
}) {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const {
    scanResult,
    clearScanResult,
    discardScanResult,
    pushScanTarget,
    popScanTarget,
    activeScanTarget,
    hardwareScanner,
    scanMode,
    cameraBlocked,
    cameraGranted
  } = useScanner();
  const laserMode = scanMode === SCAN_MODE.LASER;
  // The read already in the provider when the overlay opened is the Pick / Verify screen's, never
  // this overlay's pick ticket.
  const handledScanRef = useRef(scanResult);
  const settledRef = useRef(false);
  // A scan, the Enter key and the button can all land before the re-render shows "Combining".
  const inFlightRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'scanning' });
  const [typed, setTyped] = useState('');

  // Claimed once for the overlay's whole life. The scanner's functions are read through a ref: an
  // effect keyed on their identities would hand the camera back and forth on every re-render of a
  // provider that does not memoize them.
  const scannerFnsRef = useRef({ pushScanTarget, popScanTarget, discardScanResult });
  scannerFnsRef.current = { pushScanTarget, popScanTarget, discardScanResult };
  useEffect(() => {
    scannerFnsRef.current.pushScanTarget(targetRef, SCAN_PROFILE.HOME);
    return () => {
      const fns = scannerFnsRef.current;
      fns.popScanTarget(targetRef);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      // Drop an unconsumed read so the screen underneath does not take a pick ticket as an item.
      fns.discardScanResult();
    };
  }, []);

  const submit = useCallback(
    async (raw: string) => {
      const code = raw.trim();
      if (!code || settledRef.current || inFlightRef.current) return;
      inFlightRef.current = true;
      setStatus({ kind: 'combining', code });
      const result = await onCombine(code);
      inFlightRef.current = false;
      if (!result.ok) {
        buzz([60, 80, 60]);
        playFailTone();
        setStatus({ kind: 'rejected', code, message: result.message });
        return;
      }
      buzz(40);
      playSuccessTone();
      settledRef.current = true;
      setStatus({
        kind: 'combined',
        orderNumber: result.combinedOrderNumber || code,
        alreadyCombined: !!result.alreadyCombined
      });
      closeTimerRef.current = setTimeout(onClose, COMBINE_CONFIRM_MS);
    },
    [onClose, onCombine]
  );

  const ownsCamera = activeScanTarget === targetRef;
  useEffect(() => {
    if (!scanResult || !ownsCamera || settledRef.current) return;
    if (handledScanRef.current === scanResult) return;
    handledScanRef.current = scanResult;
    // Re-arm quickly: a misread pick ticket is re-scanned straight away.
    clearScanResult(900);
    if (inFlightRef.current) return;
    const code = pickTicketCodeFromScan(scanResult);
    if (!code) {
      buzz([60, 80, 60]);
      playFailTone();
      setStatus({
        kind: 'rejected',
        code: scanResult.text || '',
        message: 'That is a product barcode. Scan the barcode on the other order’s pick ticket.'
      });
      return;
    }
    void submit(code);
  }, [scanResult, ownsCamera, clearScanResult, submit]);

  const cameraMessage = cameraBlocked
    ? 'The camera is blocked. Type the SO number below, or switch to Laser.'
    : cameraGranted
      ? 'Starting the camera…'
      : 'Allow RxERP to use your device’s camera.';

  return createPortal(
    <div
      className="zone-scan combine-scan"
      role="dialog"
      aria-modal="true"
      aria-label="Combine orders — scan the pick ticket"
    >
      <div className="zone-scan__header">
        <div className="zone-scan__titles">
          <h2>Combine orders</h2>
          <p>
            Scan the other order’s pick ticket{clientName ? ` for ${clientName}` : ''}, or type
            its SO number.
          </p>
        </div>
        <button
          type="button"
          className="zone-scan__close"
          onClick={onClose}
          aria-label="Close the pick ticket scanner"
        >
          <FiX />
        </button>
      </div>

      <div className="zone-scan__viewport">
        {!laserMode && (
          <div className="zone-scan__no-camera">
            <FiCameraOff />
            <span>{cameraMessage}</span>
          </div>
        )}
        {/* Stays mounted in both modes — this element is what claims the scanner. */}
        <div className="zone-scan__camera" ref={targetRef} hidden={laserMode} />
        {laserMode && (
          <LaserModePanel
            detected={!!hardwareScanner?.detected}
            hint="Pull the trigger to scan the pick ticket"
          />
        )}
        <ScanModeSwitch className="zone-scan__mode-switch" />
        {status.kind === 'scanning' && !laserMode && (
          <div className="zone-scan__prompt">Point the camera at the pick ticket barcode</div>
        )}
        {status.kind === 'combining' && (
          <div className="zone-scan__banner zone-scan__banner--busy" role="status">
            <FiLink />
            <div>
              <strong>Combining “{status.code}”…</strong>
            </div>
          </div>
        )}
        {status.kind === 'combined' && (
          <div className="zone-scan__banner zone-scan__banner--ok" role="status">
            <FiCheckCircle />
            <div>
              <strong>SO #{status.orderNumber}</strong>
              <span>
                {status.alreadyCombined
                  ? 'Already in this shipment'
                  : 'Combined — it ships with this order'}
              </span>
            </div>
          </div>
        )}
        {status.kind === 'rejected' && (
          <div className="zone-scan__banner zone-scan__banner--bad" role="alert">
            <FiAlertTriangle />
            <div>
              <strong>Not combined</strong>
              <span>{status.message}</span>
            </div>
            <Button type="secondary" {...click(() => setStatus({ kind: 'scanning' }))}>
              Scan again
            </Button>
          </div>
        )}
      </div>

      {expected.length > 0 && (
        <div className="zone-scan__allowed">
          <strong>Still being picked</strong>
          <span>{expected.map(soLabel).join(', ')}</span>
        </div>
      )}

      <form
        className="zone-scan__footer combine-scan__entry"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(typed);
        }}
      >
        <label className="combine-scan__label" htmlFor="combine-scan-code">
          SO number
        </label>
        <div className="combine-scan__row">
          <input
            id="combine-scan-code"
            className="combine-scan__input"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder="e.g. 12345"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            enterKeyHint="go"
            disabled={status.kind === 'combining' || status.kind === 'combined'}
          />
          <Button
            type="primary"
            disabled={!typed.trim() || status.kind === 'combining' || status.kind === 'combined'}
            {...click(() => void submit(typed))}
          >
            Combine
          </Button>
        </div>
      </form>
    </div>,
    document.body
  );
}

export default CombineScanOverlay;
